/**
 * SamVision Cabinet — entry point.
 *
 * The whole app is two layers: Providers (safe-area, persisted query client, i18n, and
 * the one-time onUnauthorized wiring) around RootNavigator (which mounts the auth stack,
 * onboarding, or the context tabs depending on the session).
 *
 * Above the navigator live the three things that must outlive whatever screen is under
 * them: the offline strip, the install offer and the toast host — an action can close
 * the very screen it was started from, and the word about how it went has to survive
 * that.
 *
 * When the offline strip shows, it clears the status bar itself and the navigator's top
 * inset is zeroed for its children, so the header sits flush under the strip rather than
 * leaving a status-bar-height gap.
 *
 * @format
 */
import React, {useEffect} from 'react';
import {StatusBar, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

import {Providers} from '@/app/providers';
import {RootNavigator} from '@/app/navigation';
import {OfflineBanner} from '@/widgets/offline-banner/OfflineBanner';
import {InstallPrompt} from '@/widgets/pwa/InstallPrompt';
import {UpdateBanner} from '@/widgets/pwa/UpdateBanner';
import {usePwa} from '@/shared/lib/pwa';
import {useIsOffline} from '@/shared/lib/use-network';
import {ErrorBoundary} from '@/shared/ui/error-boundary';
import {applyDocumentTheme} from '@/shared/ui/theme-document';
import {useIsDark} from '@/shared/ui/theme';
import {ToastHost} from '@/shared/ui/toast';
import {useThemeMode, useThemeModeStore} from '@/shared/ui/theme-mode';

/** Inside Providers, so it may read safe-area insets and the query client. */
function AppShell() {
  const {t} = useTranslation();
  const isDark = useIsDark();
  const mode = useThemeMode();
  const hydrateThemeMode = useThemeModeStore(s => s.hydrate);
  const insets = useSafeAreaInsets();
  const offline = useIsOffline();
  const {updateReady} = usePwa();

  // The stored light/dark/system choice, read once on cold start. Until it lands the
  // app renders in `system`, which is both the default and the right guess — so there
  // is no flash to guard against.
  useEffect(() => {
    void hydrateThemeMode();
  }, [hydrateThemeMode]);

  // Everything the app does NOT paint — the URL bar, the task-switcher card, the
  // scrollbars, the area behind the bounce — belongs to the document, and follows the
  // choice from here. No-op on native (see theme-document.ts).
  useEffect(() => {
    applyDocumentTheme(isDark ? 'dark' : 'light', mode);
  }, [isDark, mode]);

  // At most one strip is ever up. Offline wins: "you are seeing saved data" changes how
  // to read the screen, while "a new version is ready" can wait for the network to come
  // back — and stacking two full-width strips over the header eats a phone's top third.
  const strip = offline ? 'offline' : updateReady ? 'update' : 'none';

  return (
    <>
      <StatusBar barStyle={isDark ? 'light-content' : 'dark-content'} />
      {strip === 'offline' ? <OfflineBanner topInset={insets.top} /> : null}
      {strip === 'update' ? <UpdateBanner topInset={insets.top} /> : null}
      <View style={{flex: 1}}>
        {/* Whichever strip is up has already paid the top inset, so the navigator's
            children must not pay it again — that gap is what made the header float. */}
        <SafeAreaInsetsContext.Provider
          value={strip === 'none' ? insets : {...insets, top: 0}}>
          <RootNavigator />
        </SafeAreaInsetsContext.Provider>
      </View>

      {/* Above the navigator, so the offer follows the user onto whichever screen the
          app opened on — including the login screen, where "install it and sign in
          there instead" is the most useful moment to say it. */}
      <InstallPrompt />

      {/* Тосты — тоже над навигатором: действие может закрыть экран, с которого его
          начали, и сообщение о нём обязано пережить этот переход. */}
      <ToastHost closeLabel={t('actions.close')} />
    </>
  );
}

function App() {
  return (
    <Providers>
      {/* Последний рубеж. Внутри страниц стоит своя граница (см. `shared/ui/page`),
          которая ловит падение содержимого, не снимая раму; сюда доезжает только то,
          что сломалось выше неё. */}
      <ErrorBoundary testID="app-error-boundary">
        <AppShell />
      </ErrorBoundary>
    </Providers>
  );
}

export default App;
